import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { Calendar, MessageCircle, Mail } from 'lucide-react';
import ContactStepsTimeline from './contact/ContactStepsTimeline';
import ContactPhoneMockup from './contact/ContactPhoneMockup';

// Animation constants
const BUTTON_HOVER = { scale: 1.04, y: -2 };
const BUTTON_TAP = { scale: 0.96 };
const FADE_UP_HIDDEN = { opacity: 0, y: 24 };
const FADE_UP_VISIBLE = { opacity: 1, y: 0 };
const HEADER_TRANSITION = { duration: 0.6 };
const CTA_TRANSITION = { delay: 0.55, duration: 0.6 };

const CALENDAR_URL = process.env.REACT_APP_CALENDAR_URL || "https://calendar.app.google/L78UB8ScNkvG6VQF6";

const buildSteps = (t) => [
  {
    id: 'chat',
    icon: MessageCircle,
    gradient: 'from-pink-500 to-fuchsia-600',
    shadow: 'shadow-pink-500/40',
    title: t.contact.steps.chat.title,
    desc: t.contact.steps.chat.desc
  },
  {
    id: 'book',
    icon: Calendar,
    gradient: 'from-orange-500 to-amber-500',
    shadow: 'shadow-orange-500/40',
    title: t.contact.steps.book.title,
    desc: t.contact.steps.book.desc
  },
  {
    id: 'email',
    icon: Mail,
    gradient: 'from-violet-500 to-indigo-600',
    shadow: 'shadow-violet-500/40',
    title: t.contact.steps.email.title,
    desc: t.contact.steps.email.desc
  }
];

const openCalendar = () => {
  window.open(CALENDAR_URL, '_blank', 'noopener,noreferrer');
};

const Contact = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const steps = buildSteps(t);

  return (
    <section
      id="contact"
      ref={ref}
      className="relative py-16 md:py-32 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 -left-32 w-[420px] h-[420px] bg-fuchsia-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-[480px] h-[480px] bg-orange-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={FADE_UP_HIDDEN}
          animate={isInView ? FADE_UP_VISIBLE : FADE_UP_HIDDEN}
          transition={HEADER_TRANSITION}
          className="text-center mb-10 md:mb-16"
        >
          <p className="text-xs sm:text-sm md:text-base text-gray-400 tracking-wider uppercase mb-2 md:mb-3">
            {t.contact.title}
          </p>
          <h2 className="text-2xl sm:text-4xl lg:text-6xl font-bold leading-tight mb-3 md:mb-4">
            <span className="bg-gradient-to-r from-white via-orange-100 to-white bg-clip-text text-transparent">
              {t.contact.subtitle}
            </span>
          </h2>
          <p className="text-sm md:text-lg text-gray-400">{t.contact.tagline}</p>
        </motion.div>

        {/* Two-column main content */}
        <div className="grid grid-cols-2 gap-4 sm:gap-8 lg:gap-16 items-center mb-10 md:mb-16">
          <ContactStepsTimeline steps={steps} isInView={isInView} />
          <ContactPhoneMockup isInView={isInView} />
        </div>

        {/* Book a Call CTA */}
        <motion.div
          initial={FADE_UP_HIDDEN}
          animate={isInView ? FADE_UP_VISIBLE : FADE_UP_HIDDEN}
          transition={CTA_TRANSITION}
          className="flex flex-col items-center space-y-4 md:space-y-6"
        >
          <motion.button
            whileHover={BUTTON_HOVER}
            whileTap={BUTTON_TAP}
            onClick={openCalendar}
            data-testid="book-call-cta"
            className="inline-flex items-center gap-2 sm:gap-3 px-8 sm:px-12 md:px-14 py-3.5 sm:py-4 md:py-5 rounded-full bg-gradient-to-r from-orange-500 via-pink-500 to-fuchsia-500 text-white text-base sm:text-lg md:text-xl font-bold shadow-2xl shadow-pink-500/40 hover:shadow-pink-500/70 transition-shadow"
          >
            <Calendar className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={2.4} />
            <span>{t.contact.bookCall}</span>
          </motion.button>

          <div className="text-center text-gray-400 text-xs sm:text-sm md:text-base">
            <p className="mb-1">{t.contact.alternative}</p>
            <a
              href="mailto:omur@mrkdigitalco.com"
              className="text-orange-400 hover:text-orange-300 transition-colors font-medium"
              data-testid="contact-email-link"
            >
              omur@mrkdigitalco.com
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
